import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { Card } from "@components/Card";
import { CloudinaryImg } from "@components/CloudinaryImg";
import { Icon, IconDisplay, IconProps, IconSize } from "@components/Icon";
import { Link } from "@components/Link";
import { NoIndex } from "@components/NoIndex";
import { NonprofitSearchFaqList } from "@components/Nonprofit/NonprofitSearchFaqList";
import { ReferredNonprofitCard } from "@components/Nonprofit/ReferredNonprofitCard";
import { SearchSelectorInput } from "@components/SearchSelectorInput";
import { AutocompleteSection } from "@components/SearchTextInput";
import { DefaultPageLayout } from "@components/layout/DefaultPageLayout";
import { CardPageSection } from "@components/layout/PageSection";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useCallback, useState } from "react";

import { NonprofitAdminReferralCampaignResponse } from "@every.org/common/src/codecs/entities";
import {
  ClientRouteName,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { ImageFormat } from "@every.org/common/src/helpers/cloudinary";

import { useNonprofit } from "src/context/NonprofitsContext/hooks";
import { nonprofitOrUndefined } from "src/context/NonprofitsContext/selectors";
import {
  ContextNonprofit,
  NonprofitFetchStatus,
} from "src/context/NonprofitsContext/types";
import { colorCssVars } from "src/theme/color";
import { MediaSize, cssForMediaSize } from "src/theme/mediaQueries";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { textSizeCss, FontWeight } from "src/theme/text";
import { useUserExperiment } from "src/utility/abtesting";

const AVATAR_BG_CLOUDINARY_ID = "assets/claim_your_nonprofit_tpydcd";

const FEATURES: [IconProps["iconImport"], string][] = [
  [
    () => import("@components/Icon/icons/DollarIcon"),
    "Accept donations via card, mobile, crypto, stock, DAF, IRA, and more",
  ],
  [
    () => import("@components/Icon/icons/FaceIcon"),
    "No set-up fees, platform fees, contracts, or tiers",
  ],
  [
    () => import("@components/Icon/icons/ThermometerIcon"),
    "Launch fundraisers and matching campaigns",
  ],
  [
    () => import("@components/Icon/icons/ClipboardIcon"),
    "Sync and manage donor data and communications",
  ],
];

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: ${spacing.xxl};

  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      grid-template-columns: 2fr 1fr;
      grid-column-gap: ${spacing.l};
      & > h1 {
        grid-column: 1 / 3;
      }
    `,
  })};
`;

const FeaturesCard = styled(Card)`
  background: linear-gradient(
      180deg,
      rgba(0, 194, 152, 0.05) 0%,
      rgba(0, 255, 200, 0) 100%
    ),
    #ffffff;

  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${spacing.l};

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      grid-template-columns: 1fr 1fr;
    `,
  })};
`;

const FeatureItem = styled.div`
  display: flex;
  gap: ${spacing.m};
  align-items: center;

  .image-container {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 500px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 4px 8px 0 #2e34340d;
    background-color: white;
  }

  svg {
    width: 28px;
    height: 28px;
  }
`;

const AditionalContent = () => {
  const testVariant = useUserExperiment("np_get_started_text", false).groupName;

  switch (testVariant) {
    case "Visuals":
    case "Text":
      return (
        <div css={verticalStackCss.l}>
          <p>
            Claim your nonprofit now to accept donations by card, PayPal, Venmo,
            bank transfer, Apple or Google Pay, stock, crypto, DAF grants, or
            IRA — all through one profile.
          </p>
          {testVariant === "Visuals" && (
            <FeaturesCard>
              {FEATURES.map(([IconImport, text], index) => {
                return (
                  <FeatureItem key={index}>
                    <div className="image-container">
                      <Icon
                        iconImport={IconImport}
                        display={IconDisplay.ACCENT}
                        size={IconSize.LARGE}
                      />
                    </div>
                    <span>{text}</span>
                  </FeatureItem>
                );
              })}
            </FeaturesCard>
          )}
          <p>
            Built by a nonprofit, for nonprofits — trusted by 8,500+
            organizations.
          </p>
        </div>
      );
    default:
      return (
        <span>
          Help donors learn about your mission, programs, and impact by claiming
          your Every.org profile today. Approved admins can update key
          organizational details, manage branding, use our fundraising tools,
          download donor data, set disbursement preferences, and more.
          <br />
          <br />
          As a nonprofit ourselves, we’re here to serve the sector by making it
          easy and safe for your organization to raise funds online.
        </span>
      );
  }
};

interface NonprofitSearchPageProps {
  onButtonClick: (nonprofit: ContextNonprofit) => void;
  pageTitle: string;
  showPageHeader?: boolean;
  mainCardText: string;
  secondaryCardText?: string;
  ctaText?: string;
  linkText?: string;
  linkUrl?: string;
  placeholder?: string;
  campaign?: NonprofitAdminReferralCampaignResponse;
}

export const NonprofitSearchPage = ({
  onButtonClick,
  pageTitle,
  showPageHeader,
  mainCardText,
  secondaryCardText,
  placeholder,
  ctaText = "Continue",
  linkText,
  linkUrl,
  campaign,
}: NonprofitSearchPageProps) => {
  const [selectedNonprofitId, setSelectedNonprofitId] = useState<
    string | undefined
  >(undefined);

  const onSelectedChange = useCallback(
    (ids?: string[] | undefined) =>
      setSelectedNonprofitId(ids ? ids[0] : undefined),
    []
  );

  const selectedNonprofit = useNonprofit(
    selectedNonprofitId ? { slug: selectedNonprofitId } : undefined
  );

  const onClick = () => {
    const nonprofit = nonprofitOrUndefined(selectedNonprofit);
    if (nonprofit) {
      onButtonClick(nonprofit);
    }
  };

  return (
    <DefaultPageLayout
      pageTitle="Getting Started: How to Claim Your Nonprofit Profile"
      metas={{
        "og:description":
          "Find your nonprofit and claim its Every.org profile so that more potential donors can discover your organization and support its work",
      }}
      hideSearchbar
      css={[
        { backgroundColor: `var(${colorCssVars.background.normal})` },
        cssForMediaSize({
          min: MediaSize.MEDIUM,
          css: { backgroundColor: `var(${colorCssVars.background.faded})` },
        }),
      ]}
    >
      <NoIndex />
      <CardPageSection
        cardProps={{
          shadow: true,
        }}
        css={cssForMediaSize({
          min: MediaSize.MEDIUM,
          css: { marginTop: spacing.xxl },
        })}
      >
        <Grid>
          <div
            css={[
              verticalStackCss.xl,
              cssForMediaSize({
                min: MediaSize.X_LARGE,
                css: { paddingRight: spacing.xxl },
              }),
              cssForMediaSize({
                min: MediaSize.MEDIUM,
                css: {
                  borderRight: `1px solid var(${colorCssVars.dividerSoft})`,
                },
              }),
            ]}
          >
            <section
              css={[
                horizontalStackCss.m,
                { alignItems: "center", justifyContent: "flex-start" },
              ]}
            >
              <section
                css={css`
                  display: flex;
                  /* justify-content: center; */
                  /* align-items: center; */
                  height: 145px;
                  width: 180px;
                  margin: 0;
                `}
              >
                <CloudinaryImg
                  cloudinaryId={AVATAR_BG_CLOUDINARY_ID}
                  alt="background image"
                  imageFormat={ImageFormat.svg}
                  dimensions={{ width: 180, height: 145 }}
                />
              </section>
              <section
                css={[
                  verticalStackCss.s,
                  css`
                    text-align: left;
                  `,
                ]}
              >
                {showPageHeader && (
                  <h3
                    css={css`
                      font-weight: ${FontWeight.REGULAR};
                    `}
                  >
                    {pageTitle}
                  </h3>
                )}
                <h2>{mainCardText}</h2>
                {secondaryCardText && (
                  <p
                    css={css`
                      color: var(${colorCssVars.text.secondary});
                    `}
                  >
                    {secondaryCardText}
                  </p>
                )}
              </section>
            </section>
            <section
              css={css`
                width: 100%;
                display: flex;
                gap: ${spacing.l};
                flex-direction: column;
                ${cssForMediaSize({
                  min: MediaSize.MEDIUM,
                  css: css`
                    gap: ${spacing.s};
                    flex-direction: row;
                  `,
                })}
              `}
            >
              <SearchSelectorInput
                selector={AutocompleteSection.NONPROFITS}
                onSelectedChange={onSelectedChange}
                multiSelect={false}
                css={css`
                  width: 100%;
                `}
                placeholder={placeholder}
                data-tname="searchSelector"
              />
              <Button
                data-tname="nonprofitSearch-primaryAction"
                role={ButtonRole.PRIMARY}
                onClick={{
                  kind: ButtonTargetKind.FUNCTION,
                  action: onClick,
                }}
                disabled={!nonprofitOrUndefined(selectedNonprofit)}
                submitting={
                  selectedNonprofit === NonprofitFetchStatus.FETCHING_NONPROFIT
                }
              >
                {ctaText}
              </Button>
            </section>
            <AditionalContent />
            {campaign && <ReferredNonprofitCard campaign={campaign} />}
          </div>
          <div css={verticalStackCss.m}>
            <NonprofitSearchFaqList />
            {linkText && (
              <Link
                css={textSizeCss.xs}
                to={
                  linkUrl ||
                  getRoutePath({
                    format: URLFormat.ABSOLUTE,
                    name: ClientRouteName.NONPROFITS_LANDING,
                  })
                }
                data-tname="onboardingSearchLink"
              >
                {linkText}
              </Link>
            )}
          </div>
        </Grid>
      </CardPageSection>
    </DefaultPageLayout>
  );
};
